import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid,
  Chip,
} from "@mui/material";
import { BarChart, PieChart } from "@mui/x-charts";

const ProjectsPerUser = ({ projectsPerUser, loading }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (!projectsPerUser?.length) {
    return (
      <Box p={4} textAlign="center" bgcolor="white" borderRadius={6}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  const users = projectsPerUser.map((u) => u.username);
  const totalProjects = projectsPerUser.reduce((acc, curr) => acc + Number(curr.total), 0);

  // --- ATTRACTIVE VIBRANT RAINBOW PALETTE ---
  const rainbowPalette = [
    "#FF0000", "#FF7F00", "#FFD700", "#32CD32", "#00CED1", 
    "#1E90FF", "#8A2BE2", "#FF1493", "#00FF7F", "#FF4500"
  ];

  const pieData = projectsPerUser.map((u, index) => ({
    id: index,
    label: u.username,
    value: Number(u.total) || 0,
    color: rainbowPalette[index % rainbowPalette.length],
  }));

  return (
    <Card sx={{ borderRadius: 6, boxShadow: "0px 10px 30px rgba(0,0,0,0.05)", border: "1px solid #f1f5f9" }}>
      {/* Header */}
      <Box sx={{ px: 4, py: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: "1px solid #f1f5f9" }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1e293b">Workload Analysis</Typography>
          <Typography variant="body2" color="text.secondary">Distribution of projects across the team</Typography>
        </Box>
        <Chip 
          label={`Total Projects: ${totalProjects}`} 
          sx={{ bgcolor: '#1e293b', color: 'white', fontWeight: 700, px: 1, height: 40, borderRadius: 3 }} 
        />
      </Box>

      <CardContent sx={{ p: 4 }}>
        <Grid container spacing={4}>
          
          {/* LEFT — BAR CHART (Now with Dynamic Height for many users) */}
          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" fontWeight={800} color="#64748b" mb={3} sx={{ textTransform: 'uppercase' }}>
              Resource Utilization
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
                <BarChart
                  layout="horizontal"
                  // 🔥 Fix: Dynamic height based on user count (60px per row)
                  height={Math.max(400, users.length * 60)} 
                  colors={[ "#8A2BE2","#FFD700", "#00CED1","#FF0000"]} // Vibrant Status Colors
                  yAxis={[{
                      scaleType: "band",
                      data: users,
                      tickLabelStyle: { fontSize: 13, fontWeight: 600, fill: "#475569" },
                  }]}
                  series={[
                    { label: "On Time", data: projectsPerUser.map(u => Number(u.completedWithinTarget) || 0), stack: "A" },
                    { label: "Late", data: projectsPerUser.map(u => Number(u.completedAfterTarget) || 0), stack: "A" },
                    { label: "Within Due", data: projectsPerUser.map(u => Number(u.openWithinDueDate) || 0), stack: "B" },
                    { label: "Overdue", data: projectsPerUser.map(u => Number(u.openAfterDueDate) || 0), stack: "B" },
                  ]}
                  margin={{ left: 140, right: 30 }}
                  slotProps={{ legend: { labelStyle: { fontSize: 12, fontWeight: 700 } } }}
                />
            </Box>
          </Grid>

          {/* RIGHT — DONUT CHART (Legend Fixed) */}
          <Grid item xs={12} md={5}>
            <Box sx={{ 
                p: 3, 
                bgcolor: '#f8fafc', 
                borderRadius: 5, 
                border: '1px solid #f1f5f9',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}>
              <Typography variant="subtitle2" fontWeight={800} color="#64748b" mb={2} textAlign="center" sx={{ textTransform: 'uppercase' }}>
                Total Share Distribution
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <PieChart
                    series={[{
                        data: pieData,
                        innerRadius: 85,
                        outerRadius: 120,
                        paddingAngle: 2,
                        cornerRadius: 5,
                    }]}
                    height={300}
                    // 🔥 Fix: Legend is removed from chart and handled manually to prevent overflow
                    slotProps={{ legend: { hidden: true } }} 
                />
              </Box>

              {/* 🔥 CUSTOM SCROLLABLE LEGEND (For many users) */}
              <Box sx={{ 
                mt: 2, 
                maxHeight: '200px', 
                overflowY: 'auto', 
                pr: 1,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: '10px' }
              }}>
                <Grid container spacing={1}>
                    {pieData.map((item) => (
                        <Grid item xs={6} key={item.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155' }} noWrap>
                                    {item.label}: {item.value}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </CardContent>
    </Card>
  );
};

export default ProjectsPerUser;